// THE SIX FACES, from the module that owns them: what is drawn on the box here, what the
// movement prompt asks a model to name, and the geometry that turns one of those names into
// a place all read one table.
import { FACES } from './partFaces';
import type { Ends } from './meshMeasure';
import { FACE_DIR } from './faces';

type THREE_NS = typeof import('three');
type Obj3D = import('three').Object3D;
type Vec3 = import('three').Vector3;

// What gets drawn ON a mesh rather than by it: Blender's axis gizmo, and the dimension
// lines that say how big the thing actually is.
//
// Separate from meshScene because that file answers "put this GLB on screen" and these
// answer "and tell me what I am looking at". They also come and go independently — the
// thumbnails want neither, the viewer wants both — so keeping them apart is what lets
// mountMesh take them as options instead of growing two more branches.

// Blender's own axis colours, and they are worth matching exactly rather than
// approximating: anyone who reads this viewer also reads Blender, and a red Y would be
// worse than no gizmo at all. Taken from Blender's default theme (X #EE5D67,
// Y #97D33D, Z #4185E6).
// Exported: the fields that drive these axes are labelled in the same colours, and a
// heading naming an arrow it does not match is worse than no colour at all.
export const AXIS_COLOURS = { x: 0xee5d67, y: 0x97d33d, z: 0x4185e6 } as const;

// WHAT IS DRAWN OVER WHAT, among the things drawn ABOUT the parts rather than being them.
//
// Every annotation here turns its depth test off — a mark on a solid is buried inside that
// solid, so an honestly depth-tested one is visible on a screw's thread and invisible
// everywhere it matters. Which settles the question of whether they are hidden by the mesh
// and leaves the question of how they stack against EACH OTHER, and that is decided by this
// alone: same pass, so higher draws later, so higher wins.
//
// It was three numbers in two files and no statement anywhere of which was meant to be on
// top. The crosses came out above the labels, so a part whose origin sits behind its own
// "Front" plate — which is most of them, both being in the middle of the part — had a
// magenta X drawn across the word.
//
// SET ON THE THING THAT IS DRAWN, never on the group holding it. three treats a Group's
// renderOrder as a `groupOrder` compared BEFORE any child's own, so a mark whose GROUP
// claims a number beats a label whose group claims nothing however high the label asks for.
// That is not a layer, it is a second scale with the first one nested inside it.
//
// A LABEL IS ON TOP OF EVERYTHING. It is the only annotation made of WORDS: a cross with a
// label over it is still a cross you can see and place, and a label with a cross through it
// is not a word at all. Anything added here should sit below them for the same reason.
export const DRAW_ORDER = {
  // A part's own origin, in magenta.
  cross: 6,
  // The world's, in the three axis colours — above the parts' so the one point every
  // number on the page is measured from is never buried under a part's own mark.
  worldCross: 11,
  // The legend's arms, above both crosses and below the words. See `arm` below, which is
  // drawn through everything for the same reason the words are.
  gizmo: 14,
  label: 20,
} as const;

// The gizmo's corner, in DRAWING-BUFFER pixels. Bottom-left, where Blender puts nothing —
// its own is top-right, but that is where this viewer's mode buttons already are.
//
// A CEILING now rather than the size, because the same gizmo is drawn on a 96-pixel
// thumbnail. At a fixed 84 it was most of that picture, which is why thumbnails never had
// one; taking a share of the SHORT side instead makes it a 34-pixel corner mark on a tile,
// and leaves it at exactly 84 anywhere bigger — a viewer would have to be under 240 pixels
// across before the share dipped below the ceiling.
const GIZMO_MAX = 84;
const GIZMO_SHARE = 0.35;

// AND THE SAME AGAIN FOR THE LABELLED ONE, which is a different shape.
//
// "X (width)" is nine characters where "X" is one, and in French "Z (profondeur)" is
// fourteen. Three of those around a square gizmo either run off its edges or shrink to the
// point of not being worth drawing — the arrows need a unit and a half in every direction,
// the words need four units to the side and nothing above.
//
// So the labelled gizmo is a LANDSCAPE box, twice as wide as it is tall, with a frustum to
// match: ±4 across and ±2 up. The arrows come out the same size on screen as the unlabelled
// ones (about 30 pixels), and the words get the room they need in the one direction they
// need it. Half the height for the same legibility, which is what makes it affordable in
// the corner of a viewport at all.
const GIZMO_LABELLED_H = 120;
const GIZMO_LABELLED_SHARE = 0.5;
// And the width under which the words are not worth their room: the phase's own panel
// viewport is about 150 pixels tall, where a labelled gizmo would be a quarter of its width
// at nine-pixel text. Under this the bare letters are drawn instead, in the square box they
// have always had. Same viewer, same gizmo — it simply says less where less can be read.
const GIZMO_LABELLED_MIN_W = 180;

export type Gizmo = {
  // Called each frame with the main camera, before the gizmo is drawn: it copies the
  // camera's orientation so the axes turn as the object does.
  render: (mainCamera: import('three').Camera, target: Vec3) => void;
  dispose: () => void;
};

// The axis gizmo, drawn as a second scene over the corner of the first.
//
// A separate scene and camera rather than an object parented into the main one, because a
// gizmo that lived in the scene would zoom with it: the whole point is that it stays the
// same size and only ever turns. It is rendered in its own viewport after the main pass,
// with the depth buffer cleared so the object cannot occlude it.
//
// `words` labels each arrow with its letter AND what that axis measures — "X (width)" — in
// the axis's own colour, turning with it. WHICH ARROW IS THE HEIGHT is the question the
// gizmo is looked at to answer, and three coloured letters answer it only for somebody who
// already knows the convention. The words were a fixed key in the page beside the viewport
// for a day, which is legible and says nothing about which arrow is which once the object
// has been turned; on the arrows they cannot be read against the wrong one.
//
// Omitted, the arrows are drawn bare — a thumbnail's gizmo, where at a third of a 96-pixel
// tile even the letters are a few pixels of noise around the thing they name, and the
// colours (Blender's own) already say which axis is which.
export type GizmoOpts = {
  // HOW BIG, as a multiple of the sizes above. 1 for a viewer where the gizmo is a bearing
  // glanced at in the corner; larger where it is a control rather than a compass — the
  // assembly phase writes every one of its numbers in these axes, so its gizmo is read as
  // often as the object is. It scales the CAPS and not the labels' own resolution, which is
  // a 44-pixel font on its own canvas: bigger there is sharper, not blown up.
  scale?: number;
  // Whether the labels sit on white plates. See textSprite. On by default because the
  // mesh viewer's labels hang over its object as often as over its background; off where
  // the plates were judged to cost more than they bought.
  plate?: boolean;
  // Solid arms instead of one-pixel lines, and longer. For a gizmo that is READ rather than
  // glanced at — see the arm builder, and note that line thickness is not something WebGL
  // will do for you, so this is geometry and not a width.
  bold?: boolean;
};

// How far the bold arms reach, and where the labels sit beyond them.
//
// The anchors are capped by the frustum, which is ±4.4 across and ±2.2 UP (see the render),
// and it is the vertical that binds: a label is a little over 0.6 units tall whatever it
// says, so anchoring at 1.8 puts its top at about 2.11 — inside, with not much to spare.
// Sideways there is room, the widest of them being some 3.2 units wide and so reaching
// about 3.4 from the middle. Push these further out and a label leaves the box whenever its
// own axis points up the screen.
const AXIS_LEN = 1.35;
const BOLD_TITLE_AT = 1.8;
const BOLD_LETTER_AT = 1.66;

// WHAT A GIZMO IS, apart from where it is drawn — three arms, their letters and their words,
// in a group one unit and a bit across.
//
// Split out because there are now two answers to "where": the mesh viewer's, which is a
// corner overlay with its own camera and a fixed size on screen, and the assembly viewer's,
// which is an object standing among the parts at a known length. Both are the same three
// arrows saying the same three things, and building them twice is how the two would drift.
type GizmoParts = { group: Obj3D; titles: Obj3D[]; plains: Obj3D[]; dispose: () => void };

function buildGizmo(
  THREE: THREE_NS,
  words: { x: string; y: string; z: string } | null | undefined,
  { plate, bold, letters }: { plate: boolean; bold: boolean; letters: boolean },
): GizmoParts {
  const group = new THREE.Group();
  const disposables: { dispose: () => void }[] = [];
  // The two label sets, kept so an overlay's render can show whichever its viewport has room
  // for. A gizmo standing in the scene has room by construction — it is as big as it is — so
  // it asks for the words alone and the bare letters are never built.
  const titles: Obj3D[] = [];
  const plains: Obj3D[] = [];
  const axes: [keyof typeof AXIS_COLOURS, Vec3][] = [
    ['x', new THREE.Vector3(1, 0, 0)],
    ['y', new THREE.Vector3(0, 1, 0)],
    ['z', new THREE.Vector3(0, 0, 1)],
  ];

  // A SOLID ARM RATHER THAN A LINE, for the bold gizmo.
  //
  // ArrowHelper draws its shaft as a THREE.Line, and a line's `linewidth` is one pixel
  // whatever it is set to — the WebGL implementations that honour it are the exception, not
  // the rule. So "thicker" is not a setting anywhere: it is a cylinder, built here, with a
  // cone on the end. Built along +Y, which is where three's cylinder stands, and turned to
  // the axis afterwards.
  // AND DRAWN THROUGH WHATEVER IS IN FRONT OF IT, which is the one thing a legend must do.
  //
  // These arms were depth-tested like the parts, on the reading that a part passing in front
  // of the widget is the right way round for something standing in the scene. It is not: the
  // words on the arms have never been depth-tested — they carry the same fact and are read the
  // same way — so a part covering the widget hid the arrows and left their labels floating
  // over the wood pointing at nothing. Half a legend is worse than none, because the half that
  // survives is the half that cannot be checked.
  //
  // It matters more since the arms became a ruler: they are longer now, and an arm long enough
  // to measure a rail is long enough to be inside it.
  //
  // `depthWrite` off with it, so the arms do not stamp their own depth into a buffer the marks
  // after them are tested against — a legend that occludes the thing it is a legend for is the
  // failure this is fixing, in the other direction.
  const arm = (dir: Vec3, colour: number, length: number, radius: number, head: number) => {
    const limb = new THREE.Group();
    const material = new THREE.MeshBasicMaterial({
      color: colour, depthTest: false, depthWrite: false,
    });
    disposables.push(material);
    const shaftLen = Math.max(0.01, length - head);
    const shaft = new THREE.CylinderGeometry(radius, radius, shaftLen, 12);
    shaft.translate(0, shaftLen / 2, 0);
    disposables.push(shaft);
    const shaftMesh = new THREE.Mesh(shaft, material);
    shaftMesh.renderOrder = DRAW_ORDER.gizmo;
    limb.add(shaftMesh);
    const cone = new THREE.ConeGeometry(radius * 2.6, head, 14);
    cone.translate(0, shaftLen + head / 2, 0);
    disposables.push(cone);
    const coneMesh = new THREE.Mesh(cone, material);
    coneMesh.renderOrder = DRAW_ORDER.gizmo;
    limb.add(coneMesh);
    limb.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    return limb;
  };

  for (const [name, dir] of axes) {
    const colour = AXIS_COLOURS[name];
    if (bold) {
      // THE POSITIVE HALF, AND NOTHING BEHIND IT. Blender draws a faint stub down the
      // negative side and it earns its place there, where an axis is a thing you orbit
      // around and both ends of it are somewhere you might be looking from.
      //
      // Here it is noise. This gizmo is a LEGEND — the arrows are the +X, +Y and +Z the
      // controls name, "comes in along +Z" and three seat fields — and a stub is half an
      // arrow pointing the way none of those mean, faint enough to read as a fourth thing
      // on a mark that is thirty pixels across.
      group.add(arm(dir, colour, AXIS_LEN, 0.05, 0.3));
    } else {
      // The positive half here too, and nothing behind it — see the note above, which was
      // written for the bold arms and is just as true of these. A faint line down the
      // negative side is Blender's, where an axis is a thing you orbit around and both ends
      // are somewhere you might be standing; on a legend thirty pixels across it is half an
      // arrow pointing the way none of the words mean.
      group.add(new THREE.ArrowHelper(dir, new THREE.Vector3(), 1, colour, 0.28, 0.16));
    }

    if (words) {
      // The letter first and the word in brackets after it, because the letter is what the
      // rest of the viewer calls this axis — the flip buttons in the corner are X, Y and Z,
      // and the measurement lines on the object are "3D width". This label is what ties the
      // two together, so it has to carry both.
      // SMALLER THAN THE BARE LETTER, because it is nine characters where that was one. At
      // the letter's own size the French labels ran off the box — an axis pointing at the
      // camera projects its label onto the middle, from where half of "Z (profondeur)" is
      // outside the scissor and simply cut off. Two thirds of the size and a frustum wide
      // enough for the longest of them at that size is what makes every label whole from
      // every angle, which matters more than a label being large.
      const label = textSprite(
        THREE, [{ text: `${name.toUpperCase()} (${words[name]})`, colour }], 0.42, 1, plate,
      );
      // Further out than the bare letter sat, to keep the plates off each other's middles.
      // They still cross when two axes point the same way on screen — unavoidable, a label
      // anchored to a projected point goes where that point goes — and the white behind the
      // text is 88% rather than opaque so what they cross stays readable through them.
      label.position.copy(dir).multiplyScalar(bold ? BOLD_TITLE_AT : 1.55);
      group.add(label);
      titles.push(label);
      disposables.push(label.material as unknown as { dispose: () => void });
      disposables.push((label.material as import('three').SpriteMaterial).map!);

      // AND THE BARE LETTER, for the viewers with no room for the words. The phase's own
      // panel viewport is about 150 pixels tall: a labelled gizmo there is a quarter of its
      // width at nine-pixel text, which is a worse answer than the letter it replaced. Both
      // are built and one is shown — the size is not known here, and is not fixed either,
      // the viewport being whatever the layout leaves it.
      if (letters) {
        const letter = textSprite(THREE, [{ text: name.toUpperCase(), colour }], 0.62, 1, plate);
        letter.position.copy(dir).multiplyScalar(bold ? BOLD_LETTER_AT : 1.42);
        group.add(letter);
        plains.push(letter);
        disposables.push(letter.material as unknown as { dispose: () => void });
        disposables.push((letter.material as import('three').SpriteMaterial).map!);
      }
    }
  }

  return {
    group,
    titles,
    plains,
    dispose: () => {
      disposables.forEach((d) => d.dispose());
      group.traverse((child) => {
        const line = child as import('three').Line;
        line.geometry?.dispose();
      });
    },
  };
}

// THE SAME THREE ARROWS, STANDING IN THE SCENE at a length that means something.
//
// The corner overlay below answers "which way is +Z" and nothing else: it is drawn at a
// fixed size through its own orthographic camera, so it holds still while the view zooms and
// an arm pointing at you is drawn as long as one across the screen. Both are right for a
// bearing and wrong for a thing standing next to a part.
//
// This one is an object. It is looked at through the same camera as the parts, so it turns
// with them, foreshortens with them, shrinks as you pull back — and its arms being `length`
// makes it a size to hold the part against as well as a direction to read.
//
// THE LABELS COME BACK OUT WITH THEIR OWN SIZES, and that is what this returns them for.
// Scaling the whole group scales the words with the arms, which is right for a widget whose
// arms are a fixed length and wrong for one whose arms are a RULER: an arm worth a metre is
// a hundred times the arm worth a centimetre, and the words on it do not want to be a hundred
// times the size — they want to stay readable, like the text beside a map's scale bar.
//
// So the caller sets each sprite's scale per frame, against the base it was built at. It
// cannot compute that base itself: a label's size comes out of the texture the text was drawn
// into, which is this module's business and nobody else's.
export function makeWorldGizmo(
  THREE: THREE_NS,
  words: { x: string; y: string; z: string } | null | undefined,
  // How long one arm is, in the scene's units.
  length: number,
): {
  group: Obj3D;
  labels: { sprite: Obj3D; base: import('three').Vector3 }[];
  dispose: () => void;
} {
  const parts = buildGizmo(THREE, words, { plate: false, bold: true, letters: false });
  parts.group.scale.setScalar(length / AXIS_LEN);
  return {
    group: parts.group,
    labels: parts.titles.map((sprite) => ({ sprite, base: sprite.scale.clone() })),
    dispose: parts.dispose,
  };
}

export function makeGizmo(
  THREE: THREE_NS,
  renderer: import('three').WebGLRenderer,
  words?: { x: string; y: string; z: string } | null,
  { scale = 1, plate = true, bold = false }: GizmoOpts = {},
): Gizmo {
  const scene = new THREE.Scene();
  // Orthographic, so the three arrows stay the same length whichever way they point. Under
  // perspective the one aimed at you is drawn longer, which reads as a longer axis.
  //
  // Its bounds are set per frame, because the labelled gizmo is a different SHAPE: wide and
  // short, ±4 across and ±2 up, matched by a 2:1 viewport so nothing is stretched. See the
  // render below, which decides between the two by how much room there is.
  const camera = new THREE.OrthographicCamera(-1.6, 1.6, 1.6, -1.6, 0.1, 100);
  const parts = buildGizmo(THREE, words, { plate, bold, letters: true });
  const { titles, plains } = parts;
  scene.add(parts.group);

  const offset = new THREE.Vector3();
  return {
    render: (mainCamera, target) => {
      // Same direction from the target as the real camera, at a fixed distance: the gizmo
      // shows the view's ORIENTATION and must be untouched by its distance.
      offset.copy(mainCamera.position).sub(target);
      if (offset.lengthSq() === 0) return;
      camera.position.copy(offset).normalize().multiplyScalar(5);
      camera.up.copy(mainCamera.up);
      camera.lookAt(0, 0, 0);

      const size = renderer.getSize(new THREE.Vector2());
      // Sized off the SHORT side, so a wide viewer does not grow it and a small tile does
      // not lose it. No pixel ratio anywhere: getSize answers in CSS pixels and setViewport
      // takes them, multiplying by the ratio itself — doing it here as well would draw the
      // gizmo at twice its size on any retina screen and leave it right on every other.
      //
      // The labelled box is 2:1 and measured against HALF the viewer's width, since it needs
      // twice as much of it — the same reasoning, applied to a frame that is no longer
      // square. Under GIZMO_LABELLED_MIN_W the words come out too small to be worth the
      // room they take, and the bare letters are drawn in a square box instead.
      const labelledH = Math.min(
        GIZMO_LABELLED_H * scale,
        Math.round(Math.min(size.x / 2, size.y) * GIZMO_LABELLED_SHARE),
      );
      const titled = titles.length > 0 && labelledH * 2 >= GIZMO_LABELLED_MIN_W;
      const h = titled
        ? labelledH
        : Math.min(GIZMO_MAX * scale, Math.round(Math.min(size.x, size.y) * GIZMO_SHARE));
      const w = titled ? h * 2 : h;
      titles.forEach((t) => { t.visible = titled; });
      plains.forEach((p) => { p.visible = !titled; });
      // The frustum follows the box: 2:1 for the words, square for the letters. Both are
      // isotropic against their own viewport, so the arrows are never stretched.
      // ±4.4 across: half of the widest label ("Z (profondeur)", 3.8 units at this size)
      // plus the 1.55 it is anchored at, so no label leaves the box whichever way the object
      // is turned. Still 2:1 against the viewport below, so nothing is stretched.
      const [across, up] = titled ? [4.4, 2.2] : [1.6, 1.6];
      if (camera.right !== across) {
        camera.left = -across;
        camera.right = across;
        camera.top = up;
        camera.bottom = -up;
        camera.updateProjectionMatrix();
      }
      const pad = Math.max(4, Math.round(h * 0.1));
      renderer.setScissorTest(true);
      renderer.setViewport(pad, pad, w, h);
      renderer.setScissor(pad, pad, w, h);
      // NOTHING IS ERASED HERE, and that is the whole of this dance.
      //
      // render() clears before it draws unless told otherwise, and inside a scissor box
      // that clear is a hole punched in the picture already on screen — a square of the
      // part simply gone, wherever the gizmo happens to sit. It went unnoticed for as long
      // as the gizmo did: on a full-size viewer the corner it lives in is white background,
      // and a white box cut into white is nothing to see. On a 96-pixel tile the object
      // crosses that corner, and the bite out of it is obvious.
      //
      // So: keep the colour, clear only the DEPTH, and clear it inside the scissor so the
      // gizmo is drawn over the part rather than occluded by it — which is the one thing
      // render() was being relied on to do.
      const autoClear = renderer.autoClear;
      renderer.autoClear = false;
      renderer.clearDepth();
      renderer.render(scene, camera);
      renderer.autoClear = autoClear;
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, size.x, size.y);
    },
    dispose: parts.dispose,
  };
}

// HOW BIG ANY OF THIS IS — the one question a 3D viewport cannot answer for itself. A part
// fills the frame whatever it measures: the camera is fitted to it, so a 6 mm grub screw
// and a 900 mm side panel are the same picture.
//
// THE BOX ITSELF, and not three arrows beside it. Eight corners, twelve edges, each edge in
// the colour of the axis it runs along, each carrying the millimetres it spans — and each of
// the six faces named.
//
// Three arrows parked outside the object were a reading you had to assemble: the height was a
// line floating to the right of the chair with nothing tying it to the chair's top or bottom,
// and the depth ran off into space at an angle no single view resolves. The box is the same
// three numbers said as a shape — the thing being measured is INSIDE it, every number is the
// distance between two visible corners, and there is no view from which it stops reading as a
// volume.
//
// TWELVE EDGES, THREE NUMBERS. Every edge is drawn; three of them carry the millimetres, and
// they are the three that meet at one corner (see LABEL_CORNER).
//
// All twelve were labelled first, on the argument that a box seen at an angle has edges facing
// you and edges behind the parts, so labelling one of the four per axis means picking per frame
// which one is not hidden — and labelling all of them is the answer with no wrong case. What
// that missed is that the four copies are not four readings: they are one number said four
// times, and a picture of a stool came back carrying "3D depth: 135 mm" three times over the
// seat and three more under it. Twelve plates competing with the object they measure cost more
// than the one case they were insuring against, which is a label you turn the view slightly to
// read.
//
// ONE MEASUREMENT PER AXIS, not two. A part has a size it is supposed to be and a size its
// mesh came out; both are drawn by makeDimensions because the gap between them is Phase 2's
// business. A set of parts mid-movement has no "supposed to be" — nobody printed the width of
// a washer halfway down a screw — so there is one number per axis, and it is the one the mesh
// actually measures.
//
// AND IT IS MEASURED AGAIN AS THEY MOVE. `update` takes the box the parts currently occupy,
// so the box grows as a screw travels out of the frame it is going into and settles when it
// seats. Cheap enough to call every frame: nothing here is rebuilt — the edges are one line
// list whose vertices are rewritten in place, and a label is only repainted when its rounded
// millimetre changes.

// THE EIGHT CORNERS AS THREE BITS. Bit 0 is x, bit 1 is y, bit 2 is z; set means the box's
// max on that axis and clear means its min. Which makes an edge a pair of corners differing in
// exactly one bit, and that bit the axis it runs along — so the twelve below are not a table
// anybody had to get right by hand.
const CORNERS = [0, 1, 2, 3, 4, 5, 6, 7];
const BIT = { x: 1, y: 2, z: 4 } as const;

const EDGES = (['x', 'y', 'z'] as const).flatMap((axis) => CORNERS
  // The four corners that do NOT have this axis's bit set: each is the low end of one edge
  // running along it, and taking only those is what gives twelve edges rather than each of
  // the twelve twice.
  .filter((c) => !(c & BIT[axis]))
  .map((c) => ({ axis, from: c, to: c | BIT[axis] })));

// WHERE THE THREE NUMBERS ARE WRITTEN: the corner with none of its bits set — every axis at
// its minimum, which in this frame is the bottom left front. The three edges leaving it are
// one per axis, so "the edges from this corner" IS "one width, one depth, one height", with
// no table to keep in step and no axis able to go unlabelled.
//
// That corner rather than another because of where the object sits in the shot: the box is
// read with the parts inside it, and the near lower corner is the one with empty picture
// around it from almost every angle a fitting is watched from. The far top corner is behind
// the parts as often as not, which is the case the twelve labels were insuring against.
const LABEL_CORNER = 0;

// HOW BIG THE WORDS ARE, as a share of the object's longest side — so a grub screw and a side
// panel both get text you can read without it swamping the thing it is about.
//
// The edge labels are SMALLER than the single set of arrows carried, and that is what makes
// twelve of them affordable: at the old size four copies of "3D height: 744 mm" up the sides
// of a chair was the chair. The face names are given a little more, being six short words
// rather than twelve long ones, and being the thing you look for when you have lost which way
// round the part is.
//
// TAKEN DOWN AGAIN, by a third, both together. The first pass sized them so the words were
// comfortably readable with the whole object in frame — which is the wrong test, because the
// box is read while you are leaning INTO a joint, and at that distance a comfortable label is
// a banner across the two parts you are trying to see meet. The ratio between the two is kept:
// what was too big was the whole legend, not one half of it.
export const EDGE_TEXT_SHARE = 0.02;
const FACE_TEXT_SHARE = 0.028;
// AND A CEILING IN THE PART'S OWN SIZE, whatever the picture is.
//
// Sizing the words by the picture is right while the parts are of a kind: a screw and a rail
// laid side by side are read from one distance, so one size of word reads on both. It stops
// being right when they differ by fifty times — a barrel nut seven millimetres across, in a
// picture with a four-hundred-millimetre rail in it, gets three plates and three numbers each
// several times its own width, and what is left on screen is the legend with the part
// somewhere underneath it. The label was describing something nobody could see any more.
//
// So one line may not be taller than this share of the part it belongs to. Everything of
// comparable size keeps the shared measure — the cap simply never binds — and the small parts
// stop being buried by words about themselves.
const OWN_TEXT_CAP = 0.22;

// And what the face names are written in: near-black, not the axis colours the numbers use.
// Slate 900 rather than pure black — the plate behind it is white, and ink at full black on
// white is the one pairing that reads as a hole in the picture. See the note above faceLabels.
const FACE_INK = 0x0f172a;

// WHAT THE SIX FACES ARE CALLED, already translated.
//
// NAMED IN THE ASSEMBLY'S FRAME AND NOT THE PART'S, which is the whole caveat and is worth
// stating here rather than in the phase: nothing in this codebase knows which face of a chair
// leg its maker calls the front. What it knows is the frame every number in the assembly
// phase is written in — x across, y into the picture, z up — and the six names below are that
// frame's own directions, fixed once against the view the viewer opens on: −y faces the
// camera at home and is therefore the front, +z is up and is therefore the top.
//
// So these are true of a part whose mesh was authored the right way up, and are turned with it
// for one that was not. That is the same assumption the gesture makes (see gestures.ts, where
// "lay flat" is a fixed quarter turn about this same x) and it is corrected in the same
// place — the part's `spin`, typed or dragged.
export type FaceWords = {
  top: string; bottom: string; front: string; back: string; left: string; right: string;
};

// WHAT A MODEL SAID ABOUT ONE FACE OF THIS PART, written under the name the box gives it.
//
// The six words on a box are the app's own — assigned by axis and sign, true of whatever
// frame the mesh arrived in — and the orientation call's whole job is to say whether they
// are true of the furniture. Its answer belongs on the same plate as the claim it is about:
// "Front / says: back" is a sentence, where the same two facts in a panel below the viewport
// are two facts somebody has to hold together while turning the part.
//
// `fits` is whether the applied turn honoured it. False rules the plate in red — see
// mutableLabel — which is the case worth walking round the part to look at.
export type FaceSaid = { text: string; fits: boolean };
export type FacesSaid = Partial<Record<keyof FaceWords, FaceSaid>>;

// WHILE THE QUESTION IS OUT WITH THE MODEL, the plates go blank — names and all.
//
// The alternative is what the box did: keep the last run's words up while a new run is in
// flight. Every one of them is a claim about which way round this part is, and that is the
// one thing nobody currently knows — the answer that put them there is being asked again
// precisely because it is in doubt. A stale "Top" over a spinning button is read as this
// run's answer arriving face by face.
//
// Blank rather than a spinner: a plate is a canvas texture repainted when its text changes,
// and an animation would mean repainting six of them every frame for the length of a call.
// Nothing on the faces says "not yet" as clearly, and it costs one boolean.
export const ASKING = 'asking';
export type FacesTold = FacesSaid | null | typeof ASKING;

// What a part is supposed to measure, as the box's second rows read it: an axis, or its two ends.
type WantedMm = {
  x: number | null; y: number | null; z: number | null;
  xBottom?: number | null; xTop?: number | null; yBottom?: number | null; yTop?: number | null;
};

export type ExtentDims = {
  group: Obj3D;
  // In the frame the group is hung in, which for the assembly viewer is the assembly's own:
  // x is the width, y the depth, z the HEIGHT. That is Blender's convention and the page's,
  // and it is the one place this file departs from the screen convention makeDimensions uses
  // — the caller hangs the group under the same rotation the parts are under, so the two
  // cannot disagree.
  // `textSpan` is what the WORDS are sized against, and it is not the box: with a box per
  // part, sizing each one's labels by its own longest side makes a screw's numbers a tenth
  // the height of the seat's beside it — unreadable at the distance a shot framed on both is
  // taken from. The caller passes the picture's own span, so every label in a scene is one
  // size. Absent, the box sizes its own, which is right when it is the only box.
  // `wanted` is what the part is SUPPOSED to measure, per axis of this box's own frame —
  // x the width, y the depth, z the height — and each edge label gains a second, quieter row
  // saying so. Phase 2's question and only Phase 2's: a mesh there is being judged against
  // the size the manual printed, while a screw halfway down a thread in Phase 5 has no
  // "supposed to be" and would carry a row of noise. Absent, the labels are one row.
  // `scaled` is whether the box is in MILLIMETRES at all. A mesh nothing could anchor — no
  // printed size, not a true-scale file — is drawn in its file's units, which are about one
  // across for a reconstruction: its edges read "height: 1 mm" as though that were measured.
  // False, each edge says "?" instead. Absent, the box is taken to be in millimetres.
  // `ends` is what the MESH measures at its bottom and its top (see measureEnds), for a part
  // whose `wanted` carries ends: the width and depth are then written twice, on the box's
  // bottom edge and on its top edge, each over its own wanted figure. Phase 2's question, like
  // the wanted row — nothing is drawn for it without `wantedWords`.
  update: (
    box: import('three').Box3,
    textSpan?: number,
    wanted?: WantedMm | null,
    scaled?: boolean,
    ends?: Ends | null,
  ) => void;
  // WHETHER ANYBODY WANTS THEM ON, which is a different question from whether there is
  // anything to measure, and the reason it is asked here rather than by setting
  // `group.visible` from outside. `update` runs on every frame of a movement and answers the
  // second question; if that were the only answer it would put the lines back the moment the
  // step played, and switching them off would hold only while nothing moved.
  show: (on: boolean) => void;
  // AND THE SIX FACE PLATES, on their own switch — two questions, two buttons.
  //
  // They came on together because they hang off one box, which is a fact about how this is
  // drawn and not about what is being asked. "How big is this part" and "which way round is
  // it" are looked at at different moments, and each one's answer is clutter over the other's:
  // six plates sit ON the object and hide exactly the shape somebody measuring wants to see.
  //
  // THE BOX ITSELF BELONGS TO NEITHER AND TO BOTH. The twelve edges are what both sets of
  // words are pinned to — a "Top" plate floating with no box under it names nothing — so it is
  // drawn while EITHER is on and gone when both are off. Which is also the only way to get a
  // clean picture of the mesh, which is what phase 2 is looking at half the time.
  showFaces: (on: boolean) => void;
  // AND THE TWELVE EDGES, on a switch of their own WHERE A CALLER HAS ONE: on, the box is drawn
  // with or without either set of words; off, it is gone and so are they, words about a box
  // that is not there naming nothing. `null` — what a box starts on — keeps the rule above:
  // drawn while either set of words is, which the assembly phase goes on relying on.
  showBox: (on: boolean | null) => void;
  // WHICH OF THE SIX NAMES THE PART IS STANDING IN FRONT OF, from where the camera is now.
  //
  // The names are sprites and a sprite is drawn over everything, so all six were legible at
  // once — three of them floating through the wood, and the two on the axis you are looking
  // down printed on top of each other. Which turns a label into a puzzle: "Left" and "Right"
  // in the same place says neither.
  //
  // A FACING TEST AND NOT A DEPTH TEST. Depth would hide a name the moment any part of the
  // mesh crossed in front of it, which on a curved rail is most angles and most of the time —
  // the label would flicker as the shot turned. Whether a face is turned away from you does
  // not flicker: it is a property of the box and the camera, and it changes once, where the
  // face goes edge-on.
  facing: (eye: { x: number; y: number; z: number }) => void;
  // WHAT A MODEL SAID ABOUT THESE FACES, or nothing. Written as a second row under each name
  // and re-read on the next `update`, which is the frame this scene redraws its labels on.
  // ASKING hides the six plates outright while a call is in flight — see FacesTold.
  said: (faces: FacesTold) => void;
  // THE SIX PLATES THEMSELVES, so a click on the canvas can be turned into a face. Given out
  // rather than hit-tested here because the raycaster, the camera and the pointer all belong
  // to the scene: this file draws, and the scene is where a press already becomes a part.
  //
  // `answered` is whether this face carries a second row, and the scene hit-tests only those.
  // A sprite is hit-tested by its whole quad — the canvas, which is far wider than the rounded
  // plate drawn inside it — so six of them are six invisible rectangles over the object, and
  // on a part nobody has asked about they would swallow presses meant for the part while
  // having nothing whatever to say. Where there IS a word under the name, the same width is
  // what makes it easy to hit.
  faceSprites: { face: keyof FaceWords; sprite: import('three').Sprite }[];
  answered: (face: string) => boolean;
  dispose: () => void;
};

export function makeExtentDims(
  THREE: THREE_NS,
  // "3D width", "3D depth", "3D height", already translated — the same words the mesh
  // viewer's own labels use, and translated in the same place. `unknown` is what an edge
  // says in place of a number when the box is not in millimetres (see `update`) — optional,
  // because a caller that only ever draws scaled boxes has no use for the word.
  // `bottomWidth` and the rest name the two ends of an axis, where a part has them.
  words: {
    width: string; depth: string; height: string; unknown?: string;
    bottomWidth?: string; topWidth?: string; bottomDepth?: string; topDepth?: string;
  },
  // And what its six faces are called. See FaceWords — including what they are named RELATIVE
  // TO, which is not the part.
  faces: FaceWords,
  // "wanted width", "wanted depth", "wanted height" and the word for an axis nothing was
  // measured on. Given, every edge label can carry its second row; withheld, the box never
  // draws one whatever `update` is passed. The words and the numbers are handed over
  // separately because they arrive from different places at different times: the words are
  // the caller's language, settled at mount, and the numbers are the part's and change with
  // the part.
  // `about` is what goes in front of the number when the size is an ESTIMATE rather than a
  // reading — "~", the same mark the parts table puts on the same fact (see formatUnits). The
  // caller decides it, because whose answer a size is lives on the size record and this module
  // is handed three numbers.
  wantedWords?: {
    width: string; depth: string; height: string; unknown: string; about?: string,
  },
): ExtentDims {
  const group = new THREE.Group();
  // Nothing has been measured yet, and a box drawn around an empty box is twelve edges of no
  // length piled at the origin.
  group.visible = false;

  // THE HALVES OF THAT VISIBILITY, KEPT APART. `wanted` is the ruler button's answer,
  // `wantedFaces` is the plates' own button, and `measurable` is the box's; each is written by
  // the only caller that knows it. Held rather than read back off `group.visible`, because
  // then none of them could tell why it was off.
  let wanted = true;
  let wantedFaces = true;
  // The edges' own switch, where the caller has one — see showBox. Null until then.
  let wantedBox: boolean | null = null;
  let measurable = false;
  // AND WHETHER THE QUESTION IS OUT WITH THE MODEL, which hides the plates whatever their
  // button says — see FacesTold. Kept beside the others because it is the fourth term in the
  // same product and `applyShown` is now the one place that multiplies them out.
  let asking = false;

  // WHAT IS ON SCREEN, from those four. Called from the switches as well as from `update`,
  // because a button pressed between two frames of a still picture would otherwise wait for a
  // movement to take effect — and in phase 2 nothing ever moves.
  // Which face names the part is between the camera and — see `facing`, which is the only
  // thing that writes it.
  const behind = FACES.map(() => false);

  const applyShown = () => {
    group.visible = (wantedBox ?? (wanted || wantedFaces)) && measurable;
    for (const label of edgeLabels) if (label) label.sprite.visible = wanted;
    topLabels.x.sprite.visible = wanted && topOn.x;
    topLabels.y.sprite.visible = wanted && topOn.y;
    faceLabels.forEach((label, i) => {
      label.sprite.visible = wantedFaces && !asking && !behind[i];
    });
  };

  const wordOf = (axis: 'x' | 'y' | 'z') => (
    axis === 'x' ? words.width : axis === 'y' ? words.depth : words.height);

  // THE SECOND ROW, or nothing. Nothing when this box was built without the words for it,
  // and nothing when the caller passed no figures — but a "?" when it passed figures and this
  // axis is not among them, which is a different answer and the one worth drawing: a part
  // whose depth nobody printed is a part whose depth cannot be checked, and a row that simply
  // vanished would read as a depth that matched.
  const wantedRow = (
    axis: 'x' | 'y' | 'z',
    mm: { x: number | null; y: number | null; z: number | null } | null | undefined,
  ) => {
    if (!wantedWords || !mm) return undefined;
    const word = axis === 'x' ? wantedWords.width : axis === 'y' ? wantedWords.depth
      : wantedWords.height;
    const v = mm[axis];
    const about = wantedWords.about ?? '';
    return `${word}: ${v != null ? `${about}${Math.round(v)} mm` : wantedWords.unknown}`;
  };

  // THE TWELVE EDGES AS ONE LINE LIST, not twelve line objects. Twenty-four vertices in one
  // buffer, one material, one draw call — and one `needsUpdate` per frame instead of twelve.
  // The colour rides on the vertices because it is the only thing that differs between edges,
  // and it is written once here: an edge's axis never changes, only its length.
  const edgeGeom = new THREE.BufferGeometry();
  const edgePos = new Float32Array(EDGES.length * 6);
  const edgeCol = new Float32Array(EDGES.length * 6);
  const rgb = new THREE.Color();
  EDGES.forEach((edge, i) => {
    rgb.setHex(AXIS_COLOURS[edge.axis]);
    // Both ends of the segment, so the edge is one flat colour rather than a gradient into
    // whatever the next one is.
    for (const end of [0, 1]) {
      edgeCol[i * 6 + end * 3] = rgb.r;
      edgeCol[i * 6 + end * 3 + 1] = rgb.g;
      edgeCol[i * 6 + end * 3 + 2] = rgb.b;
    }
  });
  edgeGeom.setAttribute('position', new THREE.BufferAttribute(edgePos, 3));
  edgeGeom.setAttribute('color', new THREE.BufferAttribute(edgeCol, 3));
  const edgeMat = new THREE.LineBasicMaterial({ vertexColors: true });
  group.add(new THREE.LineSegments(edgeGeom, edgeMat));

  // THREE LABELS, ONE PER AXIS, on the three edges meeting at LABEL_CORNER. The other nine
  // edges are drawn and carry no words — see the note above LABEL_CORNER.
  const edgeLabels = EDGES.map((edge) => {
    if (edge.from !== LABEL_CORNER) return null;
    const label = mutableLabel(THREE, AXIS_COLOURS[edge.axis]);
    group.add(label.sprite);
    return label;
  });

  // AND THE TOP ENDS, on the edges straight above the labelled width and depth. Only drawn for a
  // part whose drawing printed both ends of that axis — the edge under it then carries the
  // bottom end instead of the box's single figure. See `ends` on `update`.
  const topLabels = {
    x: mutableLabel(THREE, AXIS_COLOURS.x),
    y: mutableLabel(THREE, AXIS_COLOURS.y),
  };
  group.add(topLabels.x.sprite, topLabels.y.sprite);
  const topOn = { x: false, y: false };
  const END_KEYS = { x: ['xBottom', 'xTop'], y: ['yBottom', 'yTop'] } as const;

  // The two WANTED rows for an axis's ends, or null where the part has no pair — which is also
  // the whole test for whether this axis is drawn as two ends at all.
  const wantedEnds = (axis: 'x' | 'y', mm: WantedMm | null | undefined) => {
    if (!wantedWords || !mm) return null;
    const low = mm[END_KEYS[axis][0]];
    const high = mm[END_KEYS[axis][1]];
    if (low == null || high == null) return null;
    const word = axis === 'x' ? wantedWords.width : wantedWords.depth;
    const about = wantedWords.about ?? '';
    return {
      bottom: `${word}: ${about}${Math.round(low)} mm`,
      top: `${word}: ${about}${Math.round(high)} mm`,
    };
  };
  // The first row for one end: what the mesh measures there, or "?" without a scale to read it in.
  const endRow = (axis: 'x' | 'y', end: 0 | 1, ends: Ends | null | undefined, scaled: boolean) => {
    const word = axis === 'x'
      ? (end === 0 ? words.bottomWidth : words.topWidth) ?? words.width
      : (end === 0 ? words.bottomDepth : words.topDepth) ?? words.depth;
    const v = scaled && ends ? ends[END_KEYS[axis][end]] : null;
    return `${word}: ${v != null ? `${Math.round(v)} mm` : words.unknown ?? '?'}`;
  };

  // AND ONE PER FACE, in ink rather than in its axis's colour.
  //
  // They were coloured — the top and the bottom are the two ends of the blue arm, and saying
  // so in blue ties the word to the gizmo without a second legend. What that cost is the
  // distinction the colours were carrying everywhere else in the picture: red, green and blue
  // mean WIDTH, DEPTH and HEIGHT, and a measurement is the only thing that has one of those.
  // "Top" is not a length, and painting it the height's colour put a word in the same
  // vocabulary as the numbers without it being one of them. Black says it is a different kind
  // of thing, which is the whole of what needed saying.
  // Scratch for `facing`, which runs once per part per frame: three allocations a frame times
  // two parts is a garbage collection somebody can see in a clip.
  const faceQuat = new THREE.Quaternion();
  const faceNormal = new THREE.Vector3();
  const faceAt = new THREE.Vector3();

  const faceLabels = FACES.map(() => {
    const label = mutableLabel(THREE, FACE_INK);
    group.add(label.sprite);
    return label;
  });
  // What a model said about each of them, or nothing. Held rather than passed through
  // `update`: it changes when a call lands and `update` runs on every frame of a movement,
  // and threading an answer through the redraw would mean every caller of update knowing
  // about a question only one of them asks.
  let said: FacesTold = null;

  const size = new THREE.Vector3();
  // The eight corners, rewritten per frame and read by both loops below — the edges take two
  // each and the faces average four. Kept rather than recomputed inline so "corner 5" means
  // one point on the screen and not two roundings of it.
  const corners = CORNERS.map(() => new THREE.Vector3());

  return {
    group,
    update: (box, textSpan, wantedMm, scaled = true, ends = null) => {
      box.getSize(size);
      const span = Math.max(size.x, size.y, size.z);
      measurable = span > 1e-6;
      asking = said === ASKING;
      topOn.x = !!wantedEnds('x', wantedMm);
      topOn.y = !!wantedEnds('y', wantedMm);
      applyShown();
      // Laid out even while switched off, and only skipped when there is nothing to lay out:
      // `show` has no box to work from, so lines left un-updated behind a hidden group are
      // lines that come back measuring where the parts were the last time anybody looked.
      if (!measurable) return;
      // Both proportional to the PICTURE, not to this box: a screw and a chair drawn side by
      // side are read from one distance, so their labels are one size. Falling back to the
      // box's own span, which is the same number when there is nothing else on screen.
      const text = Math.min(
        textSpan && textSpan > 1e-6 ? textSpan : span,
        // The cap, expressed in the same units the shares are about to be applied in: the
        // bigger of the two shares decides it, so the ratio between the words and the plates
        // survives the clamp rather than being closed by it.
        (span * OWN_TEXT_CAP) / Math.max(EDGE_TEXT_SHARE, FACE_TEXT_SHARE),
      );
      const edgeText = text * EDGE_TEXT_SHARE;
      const faceText = text * FACE_TEXT_SHARE;
      const { min, max } = box;

      // THE CORNERS, straight off the three bits. Nothing is offset outward: the box IS the
      // extent, so an edge that did not touch the parts would be measuring something else.
      for (const c of CORNERS) {
        corners[c].set(
          c & BIT.x ? max.x : min.x,
          c & BIT.y ? max.y : min.y,
          c & BIT.z ? max.z : min.z,
        );
      }

      EDGES.forEach((edge, i) => {
        const a = corners[edge.from];
        const b = corners[edge.to];
        edgePos[i * 6] = a.x; edgePos[i * 6 + 1] = a.y; edgePos[i * 6 + 2] = a.z;
        edgePos[i * 6 + 3] = b.x; edgePos[i * 6 + 4] = b.y; edgePos[i * 6 + 5] = b.z;
        // The nine unlabelled edges are drawn and nothing else: the box says which way each
        // one runs by its colour, and the number it spans is written on its opposite number
        // at the corner.
        const label = edgeLabels[i];
        if (!label) return;
        // Whole millimetres. A tenth of a millimetre on a box that is being measured while
        // it moves is a digit that never stops changing and never means anything.
        const pair = edge.axis === 'z' ? null : wantedEnds(edge.axis, wantedMm);
        if (pair && edge.axis !== 'z') {
          // THE BOTTOM END ON THIS EDGE, which is the box's bottom edge; the top end on the edge
          // straight above it — the same corners with the height's bit set.
          label.set(endRow(edge.axis, 0, ends, scaled), edgeText, pair.bottom);
          const top = topLabels[edge.axis];
          top.set(endRow(edge.axis, 1, ends, scaled), edgeText, pair.top);
          top.sprite.position.copy(corners[edge.from | BIT.z]).add(corners[edge.to | BIT.z])
            .multiplyScalar(0.5);
        } else {
          label.set(
            `${wordOf(edge.axis)}: ${scaled ? `${Math.round(size[edge.axis])} mm` : words.unknown ?? '?'}`,
            edgeText,
            wantedRow(edge.axis, wantedMm),
          );
        }
        // Halfway along its own edge, so the number sits on the thing it measures.
        label.sprite.position.copy(a).add(b).multiplyScalar(0.5);
      });
      (edgeGeom.getAttribute('position') as import('three').BufferAttribute).needsUpdate = true;
      // Without this the box keeps the bounding sphere of wherever it used to be, and the
      // frustum cull drops it the moment the parts grow past that.
      edgeGeom.computeBoundingSphere();

      // Nothing on the faces while the question is out — see FacesTold, and `asking` above,
      // which is where that now shows. Laid out and painted anyway, so the frame the answer
      // lands on has nothing left to do but show them.
      const told = said === ASKING ? null : said;
      FACES.forEach((face, i) => {
        // The middle of the face: the box's centre, with the one axis the face is named for
        // pushed out to that face's own end.
        const at = faceLabels[i].sprite.position;
        at.set((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2);
        at[face.axis] = face.at === 'max' ? max[face.axis] : min[face.axis];
        const answer = told?.[face.word];
        faceLabels[i].set(faces[face.word], faceText, answer?.text, answer && !answer.fits);
      });
    },
    show: (on) => {
      wanted = on;
      applyShown();
    },
    showFaces: (on) => {
      wantedFaces = on;
      applyShown();
    },
    showBox: (on) => {
      wantedBox = on;
      applyShown();
    },
    facing: (eye) => {
      // The face's own outward direction, turned into the world by whatever pose the box is
      // wearing — the group carries the part's rotation, so a rail lying on its side has its
      // "top" pointing sideways and the test has to know that.
      group.getWorldQuaternion(faceQuat);
      let moved = false;
      FACES.forEach((face, i) => {
        const dir = FACE_DIR[face.word];
        faceNormal.set(dir[0], dir[1], dir[2]).applyQuaternion(faceQuat);
        faceAt.copy(faceLabels[i].sprite.position);
        group.localToWorld(faceAt);
        // Pointing the same way as the line from the eye to it is a face turned away. Written
        // out rather than through Vector3.sub, so the caller can hand over any {x, y, z} — a
        // camera position is one, and copying it into a vector to subtract it is a third
        // allocation per face per frame for arithmetic that is three lines.
        const away = faceNormal.x * (faceAt.x - eye.x)
          + faceNormal.y * (faceAt.y - eye.y)
          + faceNormal.z * (faceAt.z - eye.z) > 0;
        if (behind[i] !== away) { behind[i] = away; moved = true; }
      });
      // Only when something actually turned: this runs every frame, and applyShown walks
      // every label on the box.
      if (moved) applyShown();
    },
    said: (next) => { said = next; asking = next === ASKING; applyShown(); },
    faceSprites: FACES.map((face, i) => ({
      face: face.word as keyof FaceWords, sprite: faceLabels[i].sprite,
    })),
    answered: (face) => said !== ASKING && !!said?.[face as keyof FaceWords],
    dispose: () => {
      edgeGeom.dispose();
      edgeMat.dispose();
      [...edgeLabels, ...faceLabels, topLabels.x, topLabels.y].forEach((l) => l?.dispose());
    },
  };
}

export type DimLabels = {
  // Both names of each axis, and the word for a measurement nobody has. Passed in rather
  // than built here so the wording stays in the translation layer with every other string.
  //
  // TWO NAMES because a line carries two claims: what the part is SUPPOSED to be, and what
  // the mesh actually measures. Each row says which axis it is about — a bare "3D" under
  // "height" reads as a subtitle rather than as the other measurement of the same thing.
  wanted: { width: string; depth: string; height: string };
  actual: { width: string; depth: string; height: string };
  unknown: string;
};

export type Dims = {
  x: number | null; y: number | null; z: number | null;
  // A CYLINDER'S OWN TWO EXTENTS, for the parts whose size is not a box at all: a dowel, a
  // barrel nut. See wantedDims, which is where these become axes.
  bodyDiaMm?: number | null;
  // A FASTENER's one real number. "M6 x 70" pins the length exactly and says nothing about
  // the other two axes: the widest point of a screw is its head, and the head's diameter
  // is not in the designation — it is 10.0, 10.5 or 12.0 mm for an M6 depending on whether
  // the head is a cap, a button or a countersink. So a screw arrives with no axis measured
  // and one length, which is enough: see mmPerUnit.
  lengthMm?: number | null;
  // THE TWO ENDS OF THE WIDTH AND OF THE DEPTH, bottom then top, where a drawing printed both
  // (see PartSize). The box above keeps its one figure per axis; these draw on its bottom and
  // top edges instead of it.
  xBottom?: number | null;
  xTop?: number | null;
  yBottom?: number | null;
  yTop?: number | null;
  // WHICH MEASURED AXIS TO TRUST FIRST, where it is not the width. A PRODUCT's is its
  // height: the one axis every mesh of the assembled thing agrees on — a written run is Z-up
  // by construction but faces whichever way the model built it, so its x is the chair's width
  // or its depth depending on the run, and a sofa's two differ by half. See mmPerUnit.
  anchor?: 'x' | 'y' | 'z';
} | null | undefined;

// AXIS MAPPING, once, for this whole file — and there is nothing left to map.
//
// There used to be two conventions here. The manual's x/y/z are width, depth and height,
// and this viewer rendered as three does by default, +Y up, so its Y was the height and its
// Z the depth: everything below crossed between the two through one table, y and z trading
// places.
//
// THE VIEWER IS Z-UP NOW (see meshScene, which turns its root a quarter about X so a mesh
// stands the way it does in every other picture in this app), the gizmo letters its arms
// "Z (height)", and mesh_orient re-authors a file so +z is its top — the same convention
// FACES has always put "top" on. So the file's z IS the height, and the crossing became a
// swap of two axes that already agreed.
//
// What it cost while it stood: the scale buttons fetched the DEPTH target and applied it to
// the height axis, the arrow labelled "height" measured the depth extent, and a mesh with no
// measured width was scaled by matching its height figure to its depth.

// The three extents of something, in whatever units it is in.
export type Extent = { x: number; y: number; z: number };

// Millimetres per metre, which is what a glTF's units are by spec. A mesh converted from
// a supplier's CAD file (no longer made, see CAD_MODEL) is already the part at true scale,
// so this is its whole answer and nothing has to be measured to reach it.
export const MM_PER_TRUE_UNIT = 1000;

const AXES = ['x', 'y', 'z'] as const;

// Millimetres per model unit, from the first axis the manual actually measured.
//
// A generated mesh has no size. Reconstructors normalize their output to roughly one unit
// across, so the same chair comes back the same size as a screw and neither is a
// measurement of anything — only its PROPORTIONS survive. One real number is therefore
// enough to recover all three, and one is usually all a drawing prints.
//
// x first, then y, then z, because that is the order the numbers are trusted in: a width
// is measured across a face-on drawing, a height often includes a curve or a foot the
// mesh does not have.
export function mmPerUnit(size: Extent, dims: Dims): number | null {
  // Unless the record names its own anchor, which then goes first and the rest keep order.
  const first = dims?.anchor;
  const order = first ? [first, ...AXES.filter((a) => a !== first)] : AXES;
  for (const axis of order) {
    const mm = dims?.[axis];
    const extent = size[axis];
    if (mm != null && extent > 1e-6) return mm / extent;
  }
  // A fastener's length, against the mesh's LONGEST extent. Which axis that is cannot be
  // named in advance — a screw points wherever the file put it, and the manual's x/y/z
  // are a chair's width, depth and height — but a screw is longer than it is thick by
  // construction, so the longest one is the length and there is nothing to look up.
  const length = dims?.lengthMm;
  const longest = Math.max(size.x, size.y, size.z);
  if (length != null && longest > 1e-6) return length / longest;
  return null;
}

// MILLIMETRES PER FILE UNIT, which is the one number everything else here is derived from.
//
// A true-scale file is NOT fitted to the drawing: it is the more exact of the two, and
// scaling it to a figure read off a manual would be correcting a measurement with an
// estimate. So it answers from the spec — glTF is metres — and nothing is measured at all.
// Everything else is normalised to about one unit across and has to be anchored by a real
// number; see mmPerUnit.
//
// Written once because it is asked twice, by the labels and by the scale buttons, and the
// two disagreeing is the difference between an arrow that says 32 mm and a button that
// thinks the same mesh is 40.
//
// AND LAST, WHAT THE BUILD ITSELF MEASURES. A written run is built in metres out of millimetres
// the model chose, and Blender read the result back the moment it had run — so the run carries
// the real size of the very file on screen (see builtBoxOf). A barrel nut nobody has dimensioned
// used to draw "width: ?" over a mesh that knew perfectly well it was 10 mm across.
//
// AFTER THE DRAWING AND NEVER BEFORE IT. The printed number is the claim the mesh is being
// CHECKED against: anchored on its own build, a model's guess would agree with itself by
// construction and the label would stop being able to disagree with anything. What this fills is
// the case where there is no printed number at all, which is most hardware.
//
// The ratio is taken on the LONGEST extent, which is the one where rounding and a bevel matter
// least — the three should agree to a fraction of a percent, and where they do not it is the
// build that is out of square with its own numbers, not this.
export function mmScale(
  size: Extent, dims: Dims, trueScale = false, built?: BuiltBox,
): number | null {
  if (trueScale) return MM_PER_TRUE_UNIT;
  return mmPerUnit(size, dims) ?? mmFromBuilt(size, built);
}

// The whole object's box as Blender measured it, in millimetres — see builtBoxOf.
export type BuiltBox = { x: number; y: number; z: number } | null | undefined;

// WHERE PARTS ARE PUT TOGETHER, THE BUILD COMES FIRST: a model that carries its built size is drawn
// at it, and the listed size anchors only a model that carries none. The order above is for
// CHECKING a mesh against the drawing; once parts stand against each other — a joint, an assembly —
// their holes and their places are said in the frame they were built in.
export const dimsUnlessBuilt = <D>(built: BuiltBox, dims: D | null | undefined): D | null =>
  (built ? null : dims ?? null);

//
// LONGEST AGAINST LONGEST, not axis against axis: Blender measured the object as it was built,
// and the file on screen may have been turned since — an orientation answer wraps the mesh in a
// quarter turn, and a washer measured 12 × 12 × 2 would then pair its 12 with the file's 2. A
// turn moves which axis the longest is on and never how long it is.
function mmFromBuilt(size: Extent, built: BuiltBox): number | null {
  if (!built) return null;
  const unit = Math.max(size.x, size.y, size.z);
  const mm = Math.max(built.x, built.y, built.z);
  return unit > 1e-6 && mm > 1e-6 ? mm / unit : null;
}

// Whether the drawing measured any axis at all. A part with only a length is scaled by it
// and then READ off the mesh, which is a different kind of answer — see meshScene.
const anyAxis = (dims: Dims) =>
  dims != null && (dims.x != null || dims.y != null || dims.z != null);

// WHAT THE PART IS SUPPOSED TO MEASURE, on three axes, out of a size record that may not
// be written on three axes at all.
//
// A component is: the dimension read answers x, y and z off the retailer's drawing, and
// those go straight through. HARDWARE IS NOT, and that is the gap this closes — the
// dimension read skips hardware by design (a dowel is a few millimetres in a photograph of
// a chair with nothing to hold it against), so a dowel, a screw or a barrel nut arrives
// with every axis null and its real size in `lengthMm` and `bodyDiaMm`. Those never reached
// the labels, so the "wanted" row on every piece of hardware in the app read `unknown`
// while the record two panels away held the numbers.
//
// They map by SHAPE and not by name: these parts are cylinders, so the length is the axial
// way and the diameter is both of the others. Which screen axis the axis happens to be
// cannot be named in advance — a dowel points wherever the file put it — so it is read off
// the mesh, exactly as mmPerUnit already reads it.
//
// THE ODD ONE OUT, not the longest. A cylinder has two extents that match — its diameter,
// measured twice — and one that does not, and the one that does not is the axis whatever
// its size. Picking the LONGEST assumed the part is taller than it is wide, which a dowel
// and a screw are and a protective foot is not: a felt pad is 20 across by 4 thick, so the
// old rule put its 4 mm thickness on the wide axis and its 20 mm diameter on the two
// narrow ones, and every label read the part inside out.
//
// `extent` is the mesh's own size in the manual's convention, or null when the mesh has no
// scale worth trusting. Without it a cylinder's two numbers cannot be placed at all, and
// the axes stay unknown rather than being guessed onto the wrong ones.
export function wantedDims(dims: Dims, extent: Extent | null): Dims {
  if (!dims || anyAxis(dims)) return dims;
  const length = dims.lengthMm ?? null;
  const dia = dims.bodyDiaMm ?? null;
  if (!extent || (length == null && dia == null)) return dims;
  const axes = ['x', 'y', 'z'] as const;
  // How far each axis sits from the other two. The two that are the diameter are close to
  // each other and the third is not, so the largest of these three is the axial one — and
  // it says so whether the part is long and thin or flat and wide. A mesh with three equal
  // extents scores every axis alike and the first wins, which is the same arbitrary answer
  // any rule gives a sphere.
  const oddness = (a: typeof axes[number]) =>
    axes.reduce((sum, b) => sum + Math.abs(extent[a] - extent[b]), 0);
  const axial = axes.reduce((a, b) => (oddness(a) >= oddness(b) ? a : b));
  const out = { ...dims, x: dia, y: dia, z: dia };
  out[axial] = length;
  return out;
}

// What it would take to make ONE axis measure what the drawing says.
//
// `axis` is the file's, which is the gizmo's, which is the manual's: x width, y depth, z
// height. One convention, since the viewer went Z-up — see the note on SWAP's grave above.
//
// The returned vector is ready to be baked into the GLB: the two axes that were not
// chosen keep the uniform scale, and the chosen one is stretched past it to land on its
// measurement. Pressing a second axis afterwards corrects that one too.
//
// The file is written in METRES, because that is glTF's unit — a spec-level fact, not a
// convention to pick. Writing the millimetre figure straight in made Blender open a chair
// 482 metres wide, which is also what a physics engine or any other consumer would have
// believed. The display is unaffected: everything on screen is derived from the
// measurement, so a mesh in metres and the same mesh in millimetres both label 482.
export type AxisFit = {
  // What the drawing says, and what the mesh currently is, both in millimetres.
  measured: number;
  current: number;
  scale: [number, number, number];
};

const MM_PER_METRE = 1000;

export function axisFit(
  size: Extent, dims: Dims, axis: 'x' | 'y' | 'z', trueScale = false,
): AxisFit | null {
  const k = mmScale(size, dims, trueScale);
  if (!k || !(size[axis] > 1e-6)) return null;
  // THE SAME NUMBERS THE LABELS DRAW, out of the same function. This read `dims` directly,
  // which meant the arrow over the mesh said "wanted depth: 40 mm" while the button under
  // it said no measurement was read for that axis — two answers to one question because
  // two places worked it out. Hardware has no x/y/z at all, so that was every screw, dowel
  // and barrel nut in the app: labelled with a target and unable to be scaled to it.
  const wanted = wantedDims(dims, { x: size.x * k, y: size.y * k, z: size.z * k });
  const measured = wanted?.[axis];
  if (measured == null) return null;
  const scale = { x: k, y: k, z: k };
  scale[axis] = measured / size[axis];
  return {
    measured,
    current: size[axis] * k,
    scale: [
      scale.x / MM_PER_METRE,
      scale.y / MM_PER_METRE,
      scale.z / MM_PER_METRE,
    ],
  };
}

// The measurement lines around the object.
//
// WHICH AXIS IS WHICH: x is the width, y the depth, z the height — the file's, the gizmo's
// and the manual's, which are one convention since this viewer went Z-up. The labels used to
// follow a Y-up screen and trade the last two, which put the word "height" on the arrow
// measuring the depth.
//
// An axis with no measurement still gets its line, in grey, saying so. A part the drawing
// does not dimension is the normal case now, and an arrow that simply vanished would make
// "not measured" look like "not applicable" — the whole point of drawing it is that you
// can see which of the three is missing.
//
// EACH LINE CARRIES TWO NUMBERS, ALWAYS, and each names its own axis.
//
// "wanted height" is what the part is SUPPOSED to be — the size record, whatever the manual
// and the drawing between them established. "3D height" is what the mesh on screen actually
// measures. Two different claims about one part, and putting them side by side is the only
// way to see them disagree: on an axis the mesh was scaled by they match by construction,
// and everywhere else the gap is the reconstruction's error, in millimetres.
//
// BOTH ROWS ARE DRAWN EVEN WHEN ONE IS EMPTY, because which of them is missing is itself
// the answer. It used to collapse to a single row whenever the mesh was the only source —
// a converted STEP file, or a part with no axes — and that row was then the mesh's number
// wearing the drawing's label, which is the one reading that cannot be checked. A row that
// says `unknown` says the record is silent here; a row that vanished said nothing at all.
export function makeDimensions(
  THREE: THREE_NS,
  box: import('three').Box3,
  dims: Dims,
  labels: DimLabels,
  // The mesh's own size in mm, in the manual's convention. Null when nothing was measured:
  // the mesh could not be scaled, so its extents are model units and mean nothing.
  generated: Dims,
): { group: Obj3D; dispose: () => void } {
  const group = new THREE.Group();
  const disposables: { dispose: () => void }[] = [];

  const size = box.getSize(new THREE.Vector3());
  const min = box.min;
  const max = box.max;
  // How far outside the box the lines sit. Proportional to the object, so a screw and a
  // chair both get a gap you can see without the line drifting off screen.
  const gap = Math.max(size.x, size.y, size.z) * 0.12;

  // Each line runs along one axis, parked on two of the box's far edges so it never
  // crosses the object.
  const specs: {
    axis: 'x' | 'y' | 'z'; from: Vec3; to: Vec3;
    mm: number | null; gen: number | null; wanted: string; actual: string;
  }[] = [
    {
      axis: 'x',
      from: new THREE.Vector3(min.x, min.y - gap, max.z + gap),
      to: new THREE.Vector3(max.x, min.y - gap, max.z + gap),
      mm: dims?.x ?? null,
      gen: generated?.x ?? null,
      wanted: labels.wanted.width,
      actual: labels.actual.width,
    },
    {
      axis: 'y',
      from: new THREE.Vector3(max.x + gap, min.y, max.z + gap),
      to: new THREE.Vector3(max.x + gap, max.y, max.z + gap),
      mm: dims?.y ?? null,
      gen: generated?.y ?? null,
      wanted: labels.wanted.depth,
      actual: labels.actual.depth,
    },
    {
      axis: 'z',
      from: new THREE.Vector3(max.x + gap, min.y - gap, min.z),
      to: new THREE.Vector3(max.x + gap, min.y - gap, max.z),
      mm: dims?.z ?? null,
      gen: generated?.z ?? null,
      wanted: labels.wanted.height,
      actual: labels.actual.height,
    },
  ];

  // Sized against the object so the text is legible on a screw and not enormous on a
  // chair. Sprites do not scale with distance, so this is the only control there is, and
  // a label is two lines now — this is the height of ONE of them.
  const textHeight = Math.max(size.x, size.y, size.z) * 0.05;

  const GREY = 0x94a3b8;
  for (const spec of specs) {
    const known = spec.mm != null;
    // The LINE follows the wanted number, because the line is the claim being checked: an
    // axis nothing was ever supposed to measure is a grey arrow whatever the mesh happens
    // to be. Placed once and never again here — see DimLine, which is built to be moved for
    // the viewer that measures parts while they travel.
    const line = dimensionLine(THREE, known ? AXIS_COLOURS[spec.axis] : GREY);
    line.set(spec.from, spec.to, Math.max(size.x, size.y, size.z) * 0.035);
    group.add(line.group);

    // Always "wanted width: 482 mm" and never a bare "482 mm". Which axis a line runs along
    // is clear enough while you are turning the object and stops being clear the moment you
    // stop — and a screenshot of this viewer has to be readable on its own.
    //
    // Each row is coloured by whether IT has a number, not by whether the other one does.
    // The axis colour says which axis you are reading; grey says this particular source is
    // silent. A known 3D figure dimmed because the record beside it was empty would hide
    // the one measurement that axis actually has.
    const row = (name: string, mm: number | null) => ({
      text: `${name}: ${mm != null ? `${mm} mm` : labels.unknown}`,
      colour: mm != null ? AXIS_COLOURS[spec.axis] : GREY,
    });
    // WHAT IT IS, then what it is supposed to be. The mesh is the thing on screen and the
    // figure describing it belongs against it; the record is the claim being checked, and a
    // claim reads as a footnote to the measurement it is about. It also puts the two lines
    // in the order the table under the viewer uses — the number, then "(wanted: …)".
    const lines = [row(spec.actual, spec.gen), row(spec.wanted, spec.mm)];
    // Dimmed only when the whole line is a pair of blanks — nothing wanted and nothing
    // measured, which is an axis with nothing to say rather than an axis with news.
    const label = textSprite(THREE, lines, textHeight,
                             known || spec.gen != null ? undefined : 0.75);
    holdable(label, textHeight);
    label.position.copy(spec.from).add(spec.to).multiplyScalar(0.5);
    group.add(label);
    disposables.push((label.material as import('three').SpriteMaterial).map!);
    disposables.push(label.material as unknown as { dispose: () => void });
  }

  return {
    group,
    dispose: () => {
      disposables.forEach((d) => d.dispose());
      group.traverse((child) => {
        const mesh = child as import('three').Mesh;
        mesh.geometry?.dispose();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((m) => m?.dispose());
      });
    },
  };
}

// A line with a cone at each end — a measurement runs BETWEEN two points, and a single
// arrowhead would read as a direction.
//
// BUILT EMPTY AND THEN PLACED, rather than built from its two ends. The mesh viewer places
// it once and never again; the assembly viewer places it on every frame of a movement, and
// a line that could only be built would mean three geometries, six cones and two materials
// allocated sixty times a second. So `set` writes into what is already there: two vertices
// of one buffer, and a cone's position, aim and scale. The cone is built at unit height,
// which is what lets the head size be a scale instead of a new geometry.
type DimLine = {
  group: Obj3D;
  set: (from: Vec3, to: Vec3, head: number) => void;
  dispose: () => void;
};

function dimensionLine(THREE: THREE_NS, colour: number): DimLine {
  const group = new THREE.Group();
  const material = new THREE.LineBasicMaterial({ color: colour });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(6), 3));
  group.add(new THREE.Line(geometry, material));

  const coneGeom = new THREE.ConeGeometry(0.45, 1, 12);
  const coneMat = new THREE.MeshBasicMaterial({ color: colour });
  const cones = [0, 1].map(() => {
    const cone = new THREE.Mesh(coneGeom, coneMat);
    group.add(cone);
    return cone;
  });

  const up = new THREE.Vector3(0, 1, 0);
  const dir = new THREE.Vector3();
  const back = new THREE.Vector3();

  return {
    group,
    set: (from, to, head) => {
      const pos = geometry.getAttribute('position') as import('three').BufferAttribute;
      pos.setXYZ(0, from.x, from.y, from.z);
      pos.setXYZ(1, to.x, to.y, to.z);
      pos.needsUpdate = true;
      // Without this the line keeps the bounding sphere of wherever it used to be, and the
      // frustum cull drops it the moment the box grows past that.
      geometry.computeBoundingSphere();

      dir.copy(to).sub(from).normalize();
      back.copy(dir).negate();
      cones[0].position.copy(to);
      cones[0].quaternion.setFromUnitVectors(up, dir);
      cones[1].position.copy(from);
      cones[1].quaternion.setFromUnitVectors(up, back);
      cones.forEach((c) => c.scale.setScalar(head));
    },
    dispose: () => {
      geometry.dispose();
      material.dispose();
      coneGeom.dispose();
      coneMat.dispose();
    },
  };
}

// A LABEL WHOSE WORDS CAN CHANGE, which textSprite's cannot: that one measures its text,
// sizes a canvas to it and hands back a sprite, so a new number is a new canvas, a new
// texture and a new upload. Fine for a part that is measured once; sixty of them a second
// is not.
//
// So the canvas here is a FIXED size and the text is drawn into the middle of it. Repainting
// is then two fills into memory that already exists, and only when the text actually differs
// — a number that has not changed does not touch the GPU. The plate is drawn around the
// words rather than over the whole canvas, so the slack either side stays transparent.
const LABEL_FONT = 40;
const LABEL_W = 640;
const LABEL_H = 96;

// The second row's ink: grey, whatever axis it belongs to. The colour on the first row says
// WHICH measurement you are reading; repeating it underneath would say the two rows are two
// measurements, and they are one measurement and one claim about it.
const WANTED_GREY = '#94a3b8';

// AND THE INK OF A ROW THAT IS A DISAGREEMENT. Rose 500 and rose 600 — the same red every
// other refusal in this app is written in — used on the second row and around the plate when
// what it says contradicts the row above it. See faceSaid: the word the model gave a face is
// a footnote like any other until it disagrees with the one the box is drawing, and then it
// is the only thing on the picture worth looking at.
const ALERT_INK = '#f43f5e';
const ALERT_EDGE = '#e11d48';

export type MutableLabel = {
  sprite: import('three').Sprite;
  // `height` is ONE line's height in world units, as textSprite measures it. `second` is an
  // optional quieter row under the first — what the part is SUPPOSED to measure, under what
  // it does. Grey and beneath, because it is the claim being checked and the measurement is
  // the thing on screen; a footnote to the number it is about.
  //
  // `alert` turns that second row red and rules the plate in the same colour: the footnote
  // and the word above it disagree, which is a different thing from a footnote.
  set: (text: string, height: number, second?: string, alert?: boolean) => void;
  dispose: () => void;
};

// A MEASUREMENT LABEL HELD READABLE AT EVERY ZOOM. A label is sized in the scene, like the part it
// is on, so it grows and shrinks with the zoom — and close up it covered the part, far out it
// could not be read. It still follows the zoom, but one line of it stays between these many
// pixels tall on screen (see holdLabels, run before every frame).
const LABEL_MIN_PX = 11;
const LABEL_MAX_PX = 20;

// Marks a label as one to hold: `line` is one line's height in its own units, as it was sized.
function holdable(sprite: import('three').Sprite, line: number) {
  sprite.userData.held = { line, base: sprite.scale.clone() };
}

// Every visible marked label resized for this camera: its size in the scene, unless one line of
// it would be drawn smaller than LABEL_MIN_PX or bigger than LABEL_MAX_PX — then that, exactly.
export function holdLabels(
  scene: import('three').Object3D, camera: import('three').PerspectiveCamera, heightPx: number,
) {
  if (heightPx <= 0) return;
  const perUnit = heightPx / (2 * Math.tan((camera.fov * Math.PI) / 360));
  scene.traverseVisible((o) => {
    const held = o.userData.held as { line: number; base: import('three').Vector3 } | undefined;
    if (!held || !o.parent) return;
    const scale = o.parent.getWorldScale(o.scale.clone()).y;
    const dist = o.getWorldPosition(o.position.clone()).distanceTo(camera.position) || 1;
    const px = (held.line * scale * perUnit) / dist;
    const want = Math.min(LABEL_MAX_PX, Math.max(LABEL_MIN_PX, px));
    o.scale.copy(held.base).multiplyScalar(px > 0 ? want / px : 1);
  });
}

export function mutableLabel(THREE: THREE_NS, colour: number): MutableLabel {
  const canvas = document.createElement('canvas');
  canvas.width = LABEL_W;
  canvas.height = LABEL_H;
  const ctx = canvas.getContext('2d')!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // NOT TONE MAPPED, which is what makes a white plate white. The assembly's renderer runs
  // ACES filmic tone mapping — right for the meshes, which are lit — and a sprite carrying a
  // texture goes through it too unless it says otherwise. So the plate drawn at pure white
  // came out at about four fifths of it: a grey card on a white background, beside gizmo
  // labels that had no card and therefore showed the background untouched. The same
  // compression was washing the coloured text out with it.
  //
  // A label is not lit and has no business being exposed. What is drawn into the canvas is
  // what should reach the screen.
  const material = new THREE.SpriteMaterial({
    map: texture, transparent: true, depthTest: false, toneMapped: false,
  });
  const sprite = new THREE.Sprite(material);
  // Over every other annotation — see DRAW_ORDER. A plate with a cross drawn through it is
  // not a word.
  sprite.renderOrder = DRAW_ORDER.label;
  const hex = `#${colour.toString(16).padStart(6, '0')}`;
  let painted = '';

  return {
    sprite,
    set: (text, height, second, alert) => {
      const key = `${text}\n${second ?? ''}\n${alert ? '!' : ''}`;
      if (key !== painted) {
        painted = key;
        ctx.clearRect(0, 0, LABEL_W, LABEL_H);
        ctx.font = `600 ${LABEL_FONT}px ui-monospace, monospace`;
        const pad = 12;
        const rows = second ? [text, second] : [text];
        const w = Math.max(...rows.map((r) => ctx.measureText(r).width)) + pad * 2;
        const line = LABEL_FONT + 4;
        const h = line * rows.length + pad * 2;
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        const x = (LABEL_W - w) / 2;
        const y = (LABEL_H - h) / 2;
        ctx.beginPath();
        // roundRect is recent, and a thrown TypeError here would take the scene with it.
        if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, 10);
        else ctx.rect(x, y, w, h);
        ctx.fill();
        // RULED IN RED WHERE THE TWO ROWS DISAGREE. On the plate rather than on the words,
        // because it is the label as a whole that is in question and because a plate with an
        // edge on it reads at the distance a whole part is framed from, where a red word does
        // not. Drawn thick enough to survive the sprite being a fifth of its canvas on screen.
        if (alert) {
          ctx.strokeStyle = ALERT_EDGE;
          ctx.lineWidth = 5;
          ctx.stroke();
        }
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        // First row in the axis's own colour, second in grey — the measurement is the thing
        // on screen and belongs against it; what it was supposed to be is a footnote. Red
        // where the footnote contradicts it.
        rows.forEach((row, i) => {
          ctx.fillStyle = i === 0 ? hex : (alert ? ALERT_INK : WANTED_GREY);
          ctx.fillText(row, LABEL_W / 2, (LABEL_H - h) / 2 + pad + line * (i + 0.5) + 2);
        });
        texture.needsUpdate = true;
      }
      // The canvas is a line of text plus its margins, so the sprite is that much taller than
      // the words on it — and its aspect is the canvas's, fixed, so nothing stretches when a
      // number gains a digit. Sized off ONE line either way: a two-row label is a taller plate
      // inside the same canvas, not bigger writing.
      const tall = height * (LABEL_H / LABEL_FONT);
      sprite.scale.set(tall * (LABEL_W / LABEL_H), tall, 1);
      holdable(sprite, height);
    },
    dispose: () => {
      texture.dispose();
      material.dispose();
    },
  };
}

// One or two lines of text, drawn on a canvas and hung in the scene facing the camera.
//
// A sprite rather than 3D text: it stays readable from every angle, which is the one
// thing a measurement label has to do, and it costs a canvas instead of a font loader.
// `height` is ONE line's height in world units, and the sprite takes its width from the
// text: scaling both axes to one number is what stretched "482 mm" wide and squeezed
// "depth unknown" flat, since a label's aspect is whatever its words make it.
function textSprite(
  THREE: THREE_NS,
  rows: { text: string; colour: number }[],
  height: number,
  opacity = 1,
  // A WHITE PLATE UNDER THE TEXT, or the words on their own.
  //
  // The plate earns itself on a DIMENSION label, which hangs over the object as often as
  // over the background and cannot be read against one of the two without it. A gizmo's
  // labels sit outside the object nearly always — they are anchored past the ends of its
  // arms — so there the plate is three white rectangles drawn over the viewport for a
  // problem that mostly does not arise, and Blender's own gizmo manages without them.
  plate = true,
): import('three').Sprite {
  const pad = 12;
  const font = 44;
  const lineH = font + 8;
  const measure = document.createElement('canvas').getContext('2d')!;
  measure.font = `600 ${font}px ui-monospace, monospace`;
  const w = Math.ceil(Math.max(...rows.map((r) => measure.measureText(r.text).width))) + pad * 2;
  const h = lineH * rows.length + pad * 2;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  if (plate) {
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    ctx.beginPath();
    // roundRect is recent. A square plate is a fine label; a thrown TypeError would take
    // the whole scene down with it, and the fallback costs one line.
    if (typeof ctx.roundRect === 'function') ctx.roundRect(0, 0, w, h, 10);
    else ctx.rect(0, 0, w, h);
    ctx.fill();
  }
  // NOTHING BEHIND THE WORDS when there is no plate — not even the soft dark halo this
  // carried while the viewport it serves was mid grey. A halo is a shadow, and a shadow
  // under coloured text on white is a smudge: it dirties the ground it was meant to lift the
  // text off. The axis colours are Blender's, chosen to read on white, and they do.
  ctx.font = `600 ${font}px ui-monospace, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  rows.forEach((row, i) => {
    ctx.fillStyle = `#${row.colour.toString(16).padStart(6, '0')}`;
    ctx.fillText(row.text, w / 2, pad + lineH * (i + 0.5) + 2);
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    // Not tone mapped, for the reason given above: a label is drawn, not lit.
    new THREE.SpriteMaterial({
      map: texture, transparent: true, opacity, depthTest: false, toneMapped: false,
    }),
  );
  // Over every other annotation — see DRAW_ORDER. The same rule the mutable labels keep,
  // and it has to be both: these are the face names and the gizmo's words, and the crosses
  // were being drawn straight across them.
  sprite.renderOrder = DRAW_ORDER.label;
  // Kept in proportion to the plate, so a long label is wide rather than stretched, and a
  // two-line label is twice as tall rather than half-size text.
  const worldH = height * (h / lineH);
  sprite.scale.set(worldH * (w / h), worldH, 1);
  return sprite;
}
